import { SettingValueType } from "@/domain/settingCatalog.interface";

// Settings store their value/default as Mongo "Mixed" (untyped). The client is
// responsible for coercing by `type` before sending and for rendering an
// appropriate editor. These helpers convert between the stored `unknown` value
// and the string the form inputs work with.

// Turn a stored value into the string an input should show while editing.
export const settingValueToInput = (
  type: SettingValueType,
  value: unknown
): string => {
  if (value === undefined || value === null) return "";

  switch (type) {
    case "boolean":
      return value ? "true" : "false";
    case "object":
    case "array":
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return "";
      }
    case "number":
    case "string":
    default:
      return String(value);
  }
};

// Compact, single-line representation for table cells.
export const settingValueToDisplay = (
  type: SettingValueType,
  value: unknown
): string => {
  if (value === undefined || value === null || value === "") return "—";

  switch (type) {
    case "boolean":
      return value ? "true" : "false";
    case "object":
    case "array":
      try {
        return JSON.stringify(value);
      } catch {
        return "—";
      }
    default:
      return String(value);
  }
};

export type ParseResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string };

// Parse the editable string back into a typed value, reporting why it failed
// rather than throwing so callers can surface a field-level error.
export const parseSettingValue = (
  type: SettingValueType,
  raw: string
): ParseResult => {
  const trimmed = raw.trim();

  switch (type) {
    case "string":
      return { ok: true, value: raw };

    case "number": {
      if (trimmed === "") return { ok: false, error: "Enter a number" };
      const num = Number(trimmed);
      if (Number.isNaN(num)) return { ok: false, error: "Must be a number" };
      return { ok: true, value: num };
    }

    case "boolean": {
      if (trimmed === "true") return { ok: true, value: true };
      if (trimmed === "false") return { ok: true, value: false };
      return { ok: false, error: "Must be true or false" };
    }

    case "object":
    case "array": {
      if (trimmed === "") return { ok: false, error: "Enter valid JSON" };
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return { ok: false, error: "Invalid JSON" };
      }
      if (type === "array" && !Array.isArray(parsed)) {
        return { ok: false, error: "Must be a JSON array" };
      }
      if (
        type === "object" &&
        (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      ) {
        return { ok: false, error: "Must be a JSON object" };
      }
      return { ok: true, value: parsed };
    }

    default:
      return { ok: true, value: raw };
  }
};
